/**
 * ProfessionalSchedulesListPage
 * Main page for managing professional schedules
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Autocomplete,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  Refresh,
  Block,
  EventAvailable,
  InfoOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  ActionButton,
  InlineFilterBar,
  PageShell,
  DataTable,
  PrerequisiteBlock,
  type DataTableColumn,
} from '../../components/common';
import type { ActiveChip } from '../../hooks/useFilterState';
import { ProfessionalScheduleWizard, ScheduleReplicationModal, BlockScheduleModal, ScheduleReleaseWizard } from './components';
import {
  professionalScheduleService,
  ProfessionalSchedule,
  ProfessionalScheduleFilters,
  ProfessionalForSchedule,
  ClinicForSchedule,
  WEEK_DAY_NAMES,
  WeekDay,
  BulkScheduleFormData,
  ScheduleReplicationData,
} from '../../services/professionalScheduleService';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useEscolhaUnica } from '../../hooks/useEscolhaUnica';
import { useAcao } from '../../authorization/useVeredito';
import { ServiceType } from '../../types/provider';

/**
 * Grouped schedule for display - one row per professional/clinic/day combination
 */
interface GroupedSchedule {
  key: string;
  professionalId: number;
  professionalName: string;
  clinicId: number;
  clinicName: string;
  weekDay: WeekDay;
  slots: Array<{ id: number; startTime: string; endTime: string }>;
  slotDuration: number;
  isActive: boolean;
  scheduleIds: number[];
}

const scheduleColumns: DataTableColumn<GroupedSchedule>[] = [
  {
    id: 'professionalName',
    label: 'Profissional',
    primary: true,
    render: (group) => group.professionalName,
  },
  {
    id: 'clinicName',
    label: 'Clínica',
    render: (group) => group.clinicName,
  },
  {
    id: 'weekDay',
    label: 'Dia',
    render: (group) => (
      <Chip
        label={WEEK_DAY_NAMES[group.weekDay]}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    id: 'slots',
    label: 'Horários',
    render: (group) => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {group.slots.map((slot) => (
          <Chip
            key={slot.id}
            label={slot.startTime}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        ))}
      </Box>
    ),
  },
  {
    id: 'slotDuration',
    label: 'Duração',
    render: (group) => `${group.slotDuration} min`,
  },
  {
    id: 'status',
    label: 'Status',
    render: (group) => (
      <Chip
        label={group.isActive ? 'Ativo' : 'Inativo'}
        color={group.isActive ? 'success' : 'default'}
        size="small"
      />
    ),
  },
];

const ProfessionalSchedulesListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const editarDia = useAcao('professional_schedule.update', 'Editar');
  const excluirDia = useAcao('professional_schedule.delete', 'Excluir dia');

  // Data states
  const [schedules, setSchedules] = useState<ProfessionalSchedule[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalForSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState<ProfessionalScheduleFilters>({});

  // Dialog states
  const [wizardOpen, setWizardOpen] = useState(false);
  const [replicationOpen, setReplicationOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [releaseWizardOpen, setReleaseWizardOpen] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState<ProfessionalForSchedule | null>(null);
  const [selectedClinic, setSelectedClinic] = useState<ClinicForSchedule | null>(null);
  const [editingSchedules, setEditingSchedules] = useState<ProfessionalSchedule[]>([]);

  // Load schedules (only when a clinic is selected)
  const loadSchedules = useCallback(async () => {
    if (!filters.clinicId) {
      setSchedules([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const schedulesResponse = await professionalScheduleService.getSchedules({
        ...filters,
        page: page + 1,
        limit: rowsPerPage,
      });

      setSchedules(schedulesResponse.schedules);
      setTotal(schedulesResponse.total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar agendas';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, enqueueSnackbar]);

  // Load professionals (filtered by clinicId when a clinic is selected)
  const loadProfessionals = useCallback(async (clinicId?: number) => {
    try {
      const professionalsResponse = await professionalScheduleService.getProfessionals(clinicId);
      setProfessionals(professionalsResponse);
    } catch {
      // Silently fail - professionals list is not critical
    }
  }, []);

  // A lista de clínicas sai do cadastro de prestadores, que é outra permissão — e é
  // justamente o Profissional de Saúde, quem mais usa esta tela, que não a tem. Sem clínica
  // escolhida nenhuma agenda carrega, e o seletor ficava vazio sem uma linha de explicação.
  const carregarClinicas = useCallback(() => professionalScheduleService.getClinics(), []);
  const clinicas = useListaPermitida<ClinicForSchedule>(
    'provider.read',
    'Clínicas',
    carregarClinicas
  );
  const clinics = clinicas.itens;

  // Only load schedules and professionals when a clinic is selected
  useEffect(() => {
    if (!selectedClinic) return;
    loadSchedules();
    loadProfessionals(selectedClinic.id);
  }, [loadSchedules, loadProfessionals, selectedClinic]);

  // Inline filters: clinic and professional apply live, no "Filtrar" button
  const handleClinicChange = (clinic: ClinicForSchedule | null) => {
    setSelectedClinic(clinic);
    setFilters(prev => ({ ...prev, clinicId: clinic?.id, professionalId: undefined }));

    // Reset professional when clinic changes
    setSelectedProfessional(null);
  };

  const { mostrarSeletor: mostrarSeletorDeClinica } = useEscolhaUnica(
    clinics,
    selectedClinic,
    handleClinicChange,
    { resolvida: clinicas.resolvida }
  );

  const handleProfessionalChange = (professional: ProfessionalForSchedule | null) => {
    setSelectedProfessional(professional);
    setFilters(prev => ({ ...prev, professionalId: professional?.id }));
  };

  const handleClearFilters = () => {
    setSelectedClinic(null);
    setSelectedProfessional(null);
    setFilters({});
  };

  const filterChips: ActiveChip[] = [];
  if (selectedClinic) {
    filterChips.push({
      key: 'clinicId',
      label: 'Clínica',
      value: selectedClinic.tradeName,
      onDelete: () => handleClinicChange(null),
    });
  }
  if (selectedProfessional) {
    filterChips.push({
      key: 'professionalId',
      label: 'Profissional',
      value: selectedProfessional.name,
      onDelete: () => handleProfessionalChange(null),
    });
  }

  // Handle page change
  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Open wizard for new schedule
  const handleNewSchedule = () => {
    if (!selectedClinic) {
      enqueueSnackbar('Selecione uma clínica primeiro', { variant: 'warning' });
      return;
    }
    if (!selectedProfessional) {
      enqueueSnackbar('Selecione um profissional primeiro', { variant: 'warning' });
      return;
    }

    // Get existing schedules for this professional/clinic combination
    const existingSchedules = schedules.filter(
      s => s.professionalId === selectedProfessional.id && s.clinicId === selectedClinic.id
    );
    setEditingSchedules(existingSchedules);
    setWizardOpen(true);
  };

  // Handle wizard complete
  const handleWizardComplete = async (data: BulkScheduleFormData) => {
    try {
      // Delete existing schedules for this professional/clinic combination
      const existingSchedules = schedules.filter(
        s => s.professionalId === data.professionalId && s.clinicId === data.clinicId
      );

      for (const schedule of existingSchedules) {
        await professionalScheduleService.deleteSchedule(schedule.id);
      }

      // Create new schedules
      await professionalScheduleService.bulkCreate(data);

      enqueueSnackbar('Agenda salva com sucesso!', { variant: 'success' });
      setWizardOpen(false);
      loadSchedules();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar agenda';
      enqueueSnackbar(message, { variant: 'error' });
      throw err;
    }
  };

  // Handle replication
  const handleReplicationOpen = () => {
    if (!selectedProfessional) {
      enqueueSnackbar('Selecione um profissional primeiro', { variant: 'warning' });
      return;
    }
    if (!selectedClinic) {
      enqueueSnackbar('Selecione uma clínica primeiro', { variant: 'warning' });
      return;
    }
    setReplicationOpen(true);
  };

  // Handle block modal
  const handleBlockModalOpen = () => {
    if (!selectedProfessional) {
      enqueueSnackbar('Selecione um profissional primeiro', { variant: 'warning' });
      return;
    }
    if (!selectedClinic) {
      enqueueSnackbar('Selecione uma clínica primeiro', { variant: 'warning' });
      return;
    }
    setBlockModalOpen(true);
  };

  const handleBlockComplete = () => {
    enqueueSnackbar('Agenda bloqueada com sucesso!', { variant: 'success' });
    loadSchedules();
  };

  // Handle release wizard
  const handleReleaseWizardOpen = () => {
    if (!selectedProfessional) {
      enqueueSnackbar('Selecione um profissional primeiro', { variant: 'warning' });
      return;
    }
    if (!selectedClinic) {
      enqueueSnackbar('Selecione uma clinica primeiro', { variant: 'warning' });
      return;
    }
    setReleaseWizardOpen(true);
  };

  const handleReleaseComplete = () => {
    enqueueSnackbar('Agenda liberada com sucesso!', { variant: 'success' });
    loadSchedules();
  };

  const handleReplicate = async (data: ScheduleReplicationData) => {
    try {
      const result = await professionalScheduleService.replicateSchedules(data);
      enqueueSnackbar(`Agenda replicada para ${result.created} profissional(is)!`, { variant: 'success' });
      loadSchedules();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao replicar agenda';
      enqueueSnackbar(message, { variant: 'error' });
      throw err;
    }
  };

  // Group schedules by professional, clinic, and day
  const groupedSchedules = useMemo(() => {
    const groups = new Map<string, GroupedSchedule>();

    for (const schedule of schedules) {
      const key = `${schedule.professionalId}-${schedule.clinicId}-${schedule.weekDay}`;
      // Access nested professional and clinic from backend response
      const scheduleWithRelations = schedule as ProfessionalSchedule & {
        professional?: { name: string };
        clinic?: { tradeName: string };
      };
      const professionalName = scheduleWithRelations.professional?.name ?? '-';
      const clinicName = scheduleWithRelations.clinic?.tradeName ?? '-';

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          professionalId: schedule.professionalId,
          professionalName,
          clinicId: schedule.clinicId,
          clinicName,
          weekDay: schedule.weekDay as WeekDay,
          slots: [],
          slotDuration: schedule.slotDuration,
          isActive: schedule.isActive,
          scheduleIds: [],
        });
      }

      const group = groups.get(key)!;
      group.slots.push({
        id: schedule.id,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
      });
      group.scheduleIds.push(schedule.id);
    }

    // Sort slots within each group by startTime
    for (const group of groups.values()) {
      group.slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }

    // Convert to array and sort by professional name, then weekDay
    return Array.from(groups.values()).sort((a, b) => {
      const nameCompare = a.professionalName.localeCompare(b.professionalName);
      if (nameCompare !== 0) return nameCompare;
      return a.weekDay - b.weekDay;
    });
  }, [schedules]);

  // Handle delete all schedules for a professional/day group
  const handleDeleteGroup = async (group: GroupedSchedule) => {
    const dayName = WEEK_DAY_NAMES[group.weekDay];
    if (!window.confirm(`Tem certeza que deseja excluir todos os ${group.slots.length} horários de ${dayName}?`)) {
      return;
    }

    try {
      for (const scheduleId of group.scheduleIds) {
        await professionalScheduleService.deleteSchedule(scheduleId);
      }
      enqueueSnackbar(`Horários de ${dayName} excluídos com sucesso!`, { variant: 'success' });
      loadSchedules();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir horários';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Escalas de Atendimento' },
      ]}
      title="Escalas de Atendimento"
      actions={
        <>
          <Tooltip
            title={'As escalas de atendimento definem os dias e horários em que cada profissional de saúde está disponível para realizar consultas em uma determinada clínica. A partir dessas escalas, o sistema gera automaticamente os horários disponíveis para agendamento. Para cadastrar uma escala, é necessário que a clínica esteja configurada com o tipo de atendimento "Agenda dos Profissionais" e que o profissional esteja vinculado ao prestador. Selecione uma clínica abaixo, escolha o profissional e utilize o botão "Nova Agenda" para definir os dias da semana, horários de início e fim e a duração de cada consulta. Após cadastrar a escala, utilize "Liberar Agenda" para gerar os horários disponíveis para o período desejado.'}
            arrow
            placement="bottom-start"
          >
            <InfoOutlined sx={{ fontSize: '1.1rem', color: 'text.secondary', cursor: 'help', alignSelf: 'center' }} />
          </Tooltip>
          <Tooltip title="Atualizar lista">
            <IconButton onClick={() => { loadSchedules(); loadProfessionals(); clinicas.recarregar(); }} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </>
      }
    >
        {/* Inline filters: clinic and professional apply live */}
        <Box sx={{ mb: 3 }}>
          <InlineFilterBar
            resultCount={selectedClinic ? total : undefined}
            resultNoun="registros de agenda"
            chips={filterChips}
            onClearAll={handleClearFilters}
            loading={loading}
            actions={
              selectedClinic && selectedClinic.serviceType === ServiceType.AGENDA_PROFISSIONAIS ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <ActionButton
                    acao="professional_schedule.create"
                    rotulo="Liberar Agenda"
                    variant="contained"
                    color="success"
                    startIcon={<EventAvailable />}
                    onClick={handleReleaseWizardOpen}
                    disabled={!selectedProfessional}
                  >
                    Liberar Agenda
                  </ActionButton>

                  <ActionButton
                    acao="professional_schedule.update"
                    rotulo="Bloquear Agenda"
                    variant="outlined"
                    color="warning"
                    startIcon={<Block />}
                    onClick={handleBlockModalOpen}
                    disabled={!selectedProfessional}
                  >
                    Bloquear Agenda
                  </ActionButton>

                  <ActionButton
                    acao="professional_schedule.create"
                    rotulo="Replicar Agenda"
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={handleReplicationOpen}
                    disabled={!selectedProfessional}
                  >
                    Replicar Agenda
                  </ActionButton>

                  <ActionButton
                    acao="professional_schedule.create"
                    rotulo="Nova Agenda"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleNewSchedule}
                    disabled={!selectedProfessional}
                  >
                    Nova Agenda
                  </ActionButton>
                </Box>
              ) : undefined
            }
          >
            {mostrarSeletorDeClinica && (
              <Autocomplete
                options={[...clinics]}
                getOptionLabel={(option) => option.tradeName}
                value={selectedClinic}
                onChange={(_e, clinic) => handleClinicChange(clinic)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                size="small"
                sx={{ minWidth: 220 }}
                noOptionsText={clinicas.bloqueio ?? 'Nenhuma clínica cadastrada'}
                renderInput={(params) => (
                  <TextField {...params} label="Clínica" helperText={clinicas.bloqueio} />
                )}
              />
            )}
            <Autocomplete
              options={professionals}
              getOptionLabel={(option) => option.name}
              value={selectedProfessional}
              onChange={(_e, professional) => handleProfessionalChange(professional)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!selectedClinic}
              size="small"
              sx={{ minWidth: 220 }}
              renderInput={(params) => <TextField {...params} label="Profissional" />}
            />
          </InlineFilterBar>
        </Box>

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedClinic ? (
          selectedClinic.serviceType !== ServiceType.AGENDA_PROFISSIONAIS ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                A clínica <strong>{selectedClinic.tradeName}</strong> utiliza o tipo de atendimento <strong>{selectedClinic.serviceType || 'não definido'}</strong>.
              </Typography>
              <Typography variant="body2" gutterBottom>
                As escalas de atendimento estão disponíveis apenas para clínicas com o tipo <strong>{ServiceType.AGENDA_PROFISSIONAIS}</strong>.
              </Typography>
              {selectedClinic.serviceType === ServiceType.AGENDA_CLINICA ? (
                <Typography variant="body2">
                  Para editar os horários de atendimento desta clínica, acesse a <strong>listagem de prestadores</strong> e clique no ícone de relógio ao lado do prestador.
                </Typography>
              ) : selectedClinic.serviceType === ServiceType.ORDEM_CHEGADA ? (
                <Typography variant="body2">
                  Para editar os horários de atendimento desta clínica, acesse o formulário de edição do prestador na aba <strong>Configuração de Atendimento</strong>.
                </Typography>
              ) : (
                <Typography variant="body2">
                  Para configurar, acesse o cadastro do prestador e defina o campo <strong>Tipo de Atendimento</strong>.
                </Typography>
              )}
            </Alert>
          ) : selectedProfessional && !loading && schedules.length === 0 ? (
            <PrerequisiteBlock
              title="Este profissional não tem agenda nesta clínica"
              reason="A liberação de horários parte da agenda semanal do profissional, que ainda não foi configurada aqui."
              resolve={{
                label: 'Configurar agenda',
                permission: 'professional_schedule.create',
                mode: 'modal',
                onResolve: handleNewSchedule,
              }}
              deniedHint="Peça a um administrador para configurar a agenda deste profissional nesta clínica."
            />
          ) : (
            <DataTable
              columns={scheduleColumns}
              rows={groupedSchedules}
              getRowId={(group) => group.key}
              loading={loading}
              emptyState={{
                title: 'Nenhuma escala de atendimento criada ainda',
                description:
                  'A escala define os dias e horários em que cada profissional atende.',
                prereqChain: 'professional-schedules',
                action: {
                  label: 'Criar escala de atendimento',
                  node: 'professional-schedules',
                  deniedHint:
                    'Peça ao administrador da sua clínica para criar uma escala de atendimento.',
                },
              }}
              // A clínica é o recorte da tela, não um filtro: sem ela nada carrega.
              filtersActive={Boolean(filters.professionalId)}
              onClearFilters={handleClearFilters}
              actions={[
                {
                  icon: <Edit fontSize="small" />,
                  onClick: (group) => {
                    const professional = professionals.find(p => p.id === group.professionalId);
                    const clinic = clinics.find((c: ClinicForSchedule) => c.id === group.clinicId);
                    if (professional && clinic) {
                      setSelectedProfessional(professional);
                      setSelectedClinic(clinic);
                      setEditingSchedules(schedules.filter(
                        s => s.professionalId === group.professionalId && s.clinicId === group.clinicId
                      ));
                      setWizardOpen(true);
                    }
                  },
                  ...editarDia,
                },
                {
                  icon: <Delete fontSize="small" />,
                  color: 'error',
                  onClick: handleDeleteGroup,
                  ...excluirDia,
                },
              ]}
              pagination={{
                page,
                rowsPerPage,
                totalCount: total,
                onPageChange: handleChangePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions: [10, 25, 50, 100],
              }}
            />
          )
        ) : (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                Selecione uma clínica para visualizar as escalas de atendimento.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Wizard Dialog */}
        <Dialog open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Configurar Agenda
          </DialogTitle>
          <DialogContent>
            {selectedProfessional && selectedClinic && (
              <ProfessionalScheduleWizard
                professional={selectedProfessional}
                clinic={selectedClinic}
                onComplete={handleWizardComplete}
                onCancel={() => setWizardOpen(false)}
                existingSchedules={editingSchedules}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Replication Modal */}
        {selectedProfessional && selectedClinic && (
          <ScheduleReplicationModal
            open={replicationOpen}
            onClose={() => setReplicationOpen(false)}
            onReplicate={handleReplicate}
            sourceProfessional={selectedProfessional}
            availableProfessionals={professionals}
            clinic={selectedClinic}
          />
        )}

        {/* Block Schedule Modal */}
        {selectedProfessional && selectedClinic && (
          <BlockScheduleModal
            open={blockModalOpen}
            onClose={() => setBlockModalOpen(false)}
            onBlock={handleBlockComplete}
            professional={selectedProfessional}
            clinic={selectedClinic}
          />
        )}

        {/* Schedule Release Wizard */}
        {selectedProfessional && selectedClinic && (
          <ScheduleReleaseWizard
            open={releaseWizardOpen}
            onClose={() => setReleaseWizardOpen(false)}
            professionalId={selectedProfessional.id}
            professionalName={selectedProfessional.name}
            clinicId={selectedClinic.id}
            clinicName={selectedClinic.tradeName}
            companyId={undefined}
            onComplete={handleReleaseComplete}
          />
        )}
    </PageShell>
  );
};

export default ProfessionalSchedulesListPage;
